import { LegalPageLayout, P, UL, LI, Strong } from './LegalPageLayout';

/**
 * Public credit + sourcing note for the external reference datasets PlyEZ
 * uses to validate tax identifiers, compute FX, screen counterparties, and
 * produce compliant e-invoices.
 */
export function DataSourcingPage() {
  return (
    <LegalPageLayout
      i18nKey="legal.dataSourcing"
      lastUpdated="2026-06-03"
      lead={
        <>
          Several PlyEZ features rely on externally published reference data — from
          Japanese government registries to EU tax-validation services, central-bank
          FX rates, and official sanctions lists. This page documents which datasets
          we use, where they come from, how we use them, and how we attribute them.
          We use these sources to validate and enrich records; they are not a
          substitute for your own compliance review.
        </>
      }
      sections={[
        {
          id: 'corp-number',
          title: '1. 法人番号公表サイト (Corporate Number Publication Site)',
          body: (
            <>
              <P>
                <Strong>Publisher</Strong>: National Tax Agency, Japan (国税庁).<br />
                <Strong>Source</Strong>:{' '}
                <a className="text-accent-700 underline" href="https://www.houjin-bangou.nta.go.jp/" target="_blank" rel="noreferrer">
                  www.houjin-bangou.nta.go.jp
                </a>
                .<br />
                <Strong>Terms</Strong>: Government Standard Terms of Use 2.0 — Public Use (政府標準利用規約 2.0).
              </P>
              <P>
                Used by PlyEZ to validate that a 13-digit 法人番号 entered by a customer
                or supplier corresponds to a real, active Japanese legal entity, and to
                pre-fill the registered name and address. Live lookups use the NTA&rsquo;s
                public Web-API; responses are cached briefly to reduce load.
              </P>
            </>
          ),
        },
        {
          id: 'qualified-invoice',
          title: '2. 適格請求書発行事業者公表サイト (Qualified Invoice Issuer Registry)',
          body: (
            <>
              <P>
                <Strong>Publisher</Strong>: National Tax Agency, Japan (国税庁).<br />
                <Strong>Source</Strong>:{' '}
                <a className="text-accent-700 underline" href="https://www.invoice-kohyo.nta.go.jp/" target="_blank" rel="noreferrer">
                  www.invoice-kohyo.nta.go.jp
                </a>
                .<br />
                <Strong>Terms</Strong>: Government Standard Terms of Use 2.0 — Public Use.
              </P>
              <P>
                Used by PlyEZ to validate qualified-invoice registration numbers
                (T&nbsp;+&nbsp;13 digits) against the NTA registry — both when a supplier
                record is created and as a periodic re-verification job. We store the
                last-known status on the vendor record so customers can see if and when a
                registration was revoked.
              </P>
            </>
          ),
        },
        {
          id: 'vies',
          title: '3. EU VIES VAT number validation',
          body: (
            <>
              <P>
                <Strong>Publisher</Strong>: European Commission (Taxation and Customs Union).<br />
                <Strong>Source</Strong>:{' '}
                <a className="text-accent-700 underline" href="https://ec.europa.eu/taxation_customs/vies/" target="_blank" rel="noreferrer">
                  VIES VAT number validation service
                </a>
                .<br />
                <Strong>Terms</Strong>: European Commission VIES terms of use; data is
                drawn in real time from Member State VAT databases.
              </P>
              <P>
                Used to confirm that an EU VAT identification number supplied by a
                cross-border counterparty is valid and, where the Member State returns
                it, to display the associated trader name and address. VIES is a
                pass-through validation service: PlyEZ does not store the EU VAT
                databases, only the validation result and timestamp on the vendor record.
              </P>
            </>
          ),
        },
        {
          id: 'fx',
          title: '4. Foreign-exchange reference rates (ECB)',
          body: (
            <>
              <P>
                <Strong>Publisher</Strong>: European Central Bank (ECB).<br />
                <Strong>Source</Strong>:{' '}
                <a className="text-accent-700 underline" href="https://www.ecb.europa.eu/stats/policy_and_exchange_rates/euro_reference_exchange_rates/html/index.en.html" target="_blank" rel="noreferrer">
                  ECB euro foreign exchange reference rates
                </a>
                .<br />
                <Strong>Terms</Strong>: ECB reference rates are published for information
                purposes; reuse is permitted with attribution to the ECB.
              </P>
              <P>
                Used to convert and display amounts across currencies on the unified
                ledger and to record an indicative reference rate against multi-currency
                transactions. ECB reference rates are published on TARGET business days
                and are indicative, not dealing rates; the actual rate applied to a
                cross-border payout is the rate provided by the payment provider (e.g.,
                Wise or Stripe) at execution time.
              </P>
            </>
          ),
        },
        {
          id: 'sanctions',
          title: '5. Sanctions and watchlist screening',
          body: (
            <>
              <P>
                PlyEZ screens counterparties and payout recipients against official
                consolidated sanctions lists, including:
              </P>
              <UL>
                <LI>
                  <Strong>EU</Strong> — EU Consolidated Financial Sanctions List, published
                  by the European Commission (
                  <a className="text-accent-700 underline" href="https://www.sanctionsmap.eu/" target="_blank" rel="noreferrer">sanctionsmap.eu</a>
                  );
                </LI>
                <LI>
                  <Strong>United States</Strong> — OFAC Specially Designated Nationals (SDN) and
                  Consolidated Sanctions Lists, published by the U.S. Treasury (
                  <a className="text-accent-700 underline" href="https://sanctionssearch.ofac.treas.gov/" target="_blank" rel="noreferrer">sanctionssearch.ofac.treas.gov</a>
                  );
                </LI>
                <LI>
                  <Strong>United Nations</Strong> — UN Security Council Consolidated List (
                  <a className="text-accent-700 underline" href="https://www.un.org/securitycouncil/content/un-sc-consolidated-list" target="_blank" rel="noreferrer">un.org</a>
                  );
                </LI>
                <LI>
                  <Strong>Japan</Strong> — Ministry of Finance / METI lists of designated
                  parties under the Foreign Exchange and Foreign Trade Act (外為法).
                </LI>
              </UL>
              <P>
                <Strong>Terms</Strong>: these lists are official government publications
                reused with attribution to their respective publishers. Screening
                produces a risk indicator to support customer review; a match or
                non-match generated by PlyEZ is informational and does not constitute a
                legal determination. Customers remain responsible for their own sanctions
                and AML compliance.
              </P>
            </>
          ),
        },
        {
          id: 'peppol',
          title: '6. JP PINT and Peppol specifications',
          body: (
            <P>
              <Strong>Publisher</Strong>: Digital Agency, Japan (デジタル庁), acting as the
              Japan Peppol Authority, and OpenPeppol. Specifications are retrieved from{' '}
              <a className="text-accent-700 underline" href="https://www.digital.go.jp/en/policies/electronic_invoice" target="_blank" rel="noreferrer">
                digital.go.jp/en/policies/electronic_invoice
              </a>{' '}
              and the Peppol International (PINT) model documentation at{' '}
              <a className="text-accent-700 underline" href="https://docs.peppol.eu/poac/jp/" target="_blank" rel="noreferrer">
                docs.peppol.eu/poac/jp
              </a>
              . Used to build PlyEZ&rsquo;s UBL XML output conforming to the JP PINT
              billing specification.
            </P>
          ),
        },
        {
          id: 'zengin',
          title: '7. 全銀フォーマット (Zengin file format)',
          body: (
            <P>
              <Strong>Publisher</Strong>: 全国銀行協会 (Japanese Bankers Association).
              PlyEZ&rsquo;s Zengin bank-transfer file generator implements the published
              総合振込 (general transfer) format — 120 bytes per line, Shift_JIS, with
              header / data / trailer / end records. No JBA fee or licence is required for
              this implementation under the public specification.
            </P>
          ),
        },
        {
          id: 'ebookkeeping',
          title: '8. Electronic Books Preservation Act (電子帳簿保存法)',
          body: (
            <>
              <P>
                PlyEZ&rsquo;s document-retention design follows the requirements of the
                Electronic Books Preservation Act and supporting NTA guidance:
              </P>
              <UL>
                <LI>Tamper-evidence via SHA-256 file hash and a hash-chained audit log;</LI>
                <LI>Optional RFC 3161 timestamping via an accredited Japanese TSA;</LI>
                <LI>A search-keyword index for the prescribed metadata fields;</LI>
                <LI>7-year retention enforced server-side.</LI>
              </UL>
            </>
          ),
        },
      ]}
    />
  );
}
